// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A modal dialog.
 *
 * Rendered in place rather than through a portal: nothing in this app renders inside a
 * clipping or transformed container, so a fixed-position overlay escapes its parent on its
 * own, and a portal would add an indirection with nothing to buy.
 *
 * What it does carry is the behaviour a dialog is expected to have and is easy to omit:
 *
 * - **focus moves in on open and returns on close.** Without it a keyboard user is left
 *   tabbing through the page behind the overlay, which they cannot see;
 * - **Tab is trapped**, for the same reason;
 * - **Escape closes**, which is the one dismissal every dialog is expected to answer to;
 * - **the page behind does not scroll**, so dismissing does not also lose the reader's
 *   place in a long table.
 *
 * Clicking the backdrop does **not** close by default. These dialogs hold forms, and a
 * stray click outside a half-filled form is not a decision to discard it.
 *
 * All of that is set up **once, on open**. It is tempting to list `onClose` as a dependency
 * of the effect that installs it, since the effect closes over it — but callers write
 * `onClose={() => setCreating(false)}` inline, which is a new function on every render, and
 * a dialog re-renders on every keystroke it holds. The effect would then tear down and run
 * again per character, and "run again" means *move focus to the first focusable element* —
 * so typing one letter threw focus out of the field and onto the close button. The callback
 * is read through a ref instead, which keeps the behaviour current without making setup
 * depend on identity.
 *
 * Dialogs can stack — the filing dialog asks for confirmation before deleting a draft —
 * and every open one listens on the document. Only the **innermost** answers Escape and
 * Tab: otherwise one Escape in the confirmation would close the dialog under it as well,
 * with everything typed into it. Rendered in place, a stacked dialog is inside the one it
 * opened from, so "has an open dialog inside it" is what defers. (Not an open-order stack:
 * React runs a child's effects before its parent's, so a dialog nested from the first
 * render would register before the dialog it sits in.)
 */

import { type ReactNode, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

/** Open dialogs holding the page still; the first locks scrolling, the last restores it.
 * Counted rather than saved per dialog, since nested effects run inner-first and would
 * otherwise restore each other's "hidden". */
let scrollLocks = 0;
let overflowBeforeLock = "";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /** Wraps the body in a form, so Enter submits and the browser runs field validation. */
  onSubmit?: () => void;
  /**
   * For a dialog holding a table rather than a column of fields.
   *
   * The default width suits a form — a wider one only spreads its labels away from their
   * inputs. A table has the opposite problem: squeezed into that width, its columns
   * collapse into unreadable slivers. Both widths are capped against the viewport, so
   * neither runs off a narrow screen.
   */
  wide?: boolean;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  closeOnBackdrop = false,
  onSubmit,
  wide = false,
}: Props) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  // Always the render's own onClose, read only when a key is actually pressed.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    // `data-autofocus` names the control to start on — the safe answer of a confirmation,
    // say, rather than the × that happens to come first.
    (
      dialog?.querySelector<HTMLElement>("[data-autofocus]") ??
      dialog?.querySelector<HTMLElement>(FOCUSABLE)
    )?.focus();

    if (scrollLocks++ === 0) {
      overflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    function onKeyDown(event: KeyboardEvent) {
      if (dialog?.querySelector('[role="dialog"]')) return;
      if (event.key === "Escape") {
        // A popup inside the dialog (DateTimeField's calendar) closes itself first; the
        // dialog — and everything typed into it — goes only on an Escape after that.
        if ((event.target as Element | null)?.closest?.("[data-own-escape]")) return;
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      // Wrap at both ends, so focus cannot reach the page behind the overlay.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (--scrollLocks === 0) document.body.style.overflow = overflowBeforeLock;
      previouslyFocused?.focus?.();
    };
  }, []);

  const body = (
    <>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-actions">{footer}</div>}
    </>
  );

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={wide ? "modal modal-wide" : "modal"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {onSubmit ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </div>
    </div>
  );
}
